import React, { useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSingleBlog } from "./useSingleBlog";
import { useBlogs } from "./useBlogs";
import PageLoader from "../../ui/PageLoader";
import Error from "../../ui/Error";
import Author from "./Author";
import BackCircleBtn from "../../ui/BackCircleBtn";
import DetailBlogContent from "./DetailBlogContent";
import DetailBlogGallery from "./DetailBlogGallery";
import CommentSection from "./CommentSection";
import { dateFormatter } from "../../helper/DateFormatter";
import { getImageUrl } from "../../helper/imageHelper";

const DetailBlog = () => {
  const { blogId } = useParams();
  const { Blog, isLoading, isError } = useSingleBlog(blogId);
  const { filterBlogs } = useBlogs();
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const yRaw = useTransform(scrollYProgress, [0, 1], [0, 500]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  if (isLoading) return <PageLoader />;
  if (isError || !Blog) return <Error />;

  const {
    id,
    title,
    author,
    authorImage,
    createdAt,
    blogImage,
    category,
    content,
    author_linkdin,
    linkdin_followers,
    gallery,
  } = Blog;

  const heroImageUrl = getImageUrl(blogImage) || `https://placehold.co/1920x600?text=${encodeURIComponent(title)}`;

  // Filter Related Posts (Same category, excluding current)
  const relatedPosts = filterBlogs
    ?.filter(b => b.category === category && b.id !== id)
    .slice(0, 3) || [];

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-white font-primary dark:bg-bodyColor"
    >
      {/* Parallax Hero Section */}
      <div className="relative h-[70vh] min-h-[600px] w-full overflow-hidden">
        <motion.div
          style={{ y: yRaw, opacity: heroOpacity }}
          className="absolute inset-0 h-[120%] w-full"
        >
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${heroImageUrl})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90" />
        </motion.div>

        {/* Hero Content */}
        <div className="relative z-10 flex h-full items-end">
          <div className="container mx-auto px-4 pb-20 sm:px-6 lg:px-8">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="max-w-4xl space-y-8"
            >
              {/* Back Button */}
              <BackCircleBtn className="mb-4 bg-white/20 text-white hover:bg-white/30" />

              {/* Category */}
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 py-2 text-sm font-bold tracking-wide text-white backdrop-blur-md transition-colors hover:bg-white/20">
                <span className="h-2.5 w-2.5 rounded-full bg-primary animate-pulse shadow-[0_0_10px_rgba(33,150,243,0.5)]" />
                {category}
              </span>

              {/* Title */}
              <h1 className="font-secondary text-5xl font-extrabold leading-tight text-white drop-shadow-2xl sm:text-6xl lg:text-7xl">
                {title}
              </h1>

              {/* Meta Data */}
              <div className="flex flex-wrap items-center gap-6 text-sm font-medium text-gray-100 sm:text-base">
                <div className="flex items-center gap-3 rounded-full bg-black/40 px-5 py-2.5 backdrop-blur-md border border-white/10 shadow-lg">
                  <img
                    className="h-10 w-10 rounded-full border-2 border-primary object-cover"
                    src={getImageUrl(authorImage) || `https://ui-avatars.com/api/?name=${encodeURIComponent(author)}`}
                    alt={author}
                  />
                  <div className="flex flex-col leading-tight">
                    <span className="text-white font-bold">{author}</span>
                    <a href={author_linkdin} target="_blank" rel="noreferrer" className="text-xs text-primary hover:text-primary-foreground transition-colors">Connect</a>
                  </div>
                </div>

                <div className="flex items-center gap-6 bg-black/40 px-6 py-3 rounded-full backdrop-blur-md border border-white/10 shadow-lg">
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-5 w-5 text-primary">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 12h13.5A2.25 2.25 0 0 1 21 13.5v7.5" />
                    </svg>
                    {dateFormatter(createdAt)}
                  </div>
                  <div className="h-4 w-px bg-white/20" />
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-5 w-5 text-primary">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                    {Blog.readTime || 5} min read
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 py-24 sm:px-6 lg:px-8">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex flex-col gap-24"
        >
          <div className="w-full">
            <div className="prose prose-lg prose-stone dark:prose-invert max-w-none prose-headings:font-secondary prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-a:text-primary hover:prose-a:text-secondary prose-img:rounded-2xl prose-img:shadow-xl">
              <DetailBlogContent content={content} />
            </div>
          </div>

          {gallery && gallery.length > 0 && (
            <div className="my-12 w-full">
              <DetailBlogGallery gallery={gallery} />
            </div>
          )}

          <hr className="border-gray-200 dark:border-gray-800" />

          {/* Author Card */}
          <div className="w-full">
            <Author
              authorImage={imageUrl => getImageUrl(authorImage)}
              author={author}
              author_linkdin={author_linkdin}
              linkdin_followers={linkdin_followers}
            />
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mt-12 border-t border-gray-100 pt-20 dark:border-gray-800">
              <div className="mb-12 flex items-center justify-between">
                <h3 className="text-4xl font-bold text-gray-900 dark:text-white">Related Posts</h3>
                <Link to="/blog" className="text-base font-semibold text-primary hover:text-secondary hover:underline">View all posts</Link>
              </div>
              <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
                {relatedPosts.map(post => (
                  <Link key={post.id} to={`/blog/${post.id}`} className="group block space-y-5">
                    <div className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-gray-100 shadow-lg transition-all duration-300 hover:shadow-2xl">
                      <img
                        src={getImageUrl(post.blogImage)}
                        alt={post.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 transition-opacity duration-300 group-hover:opacity-80" />
                      <div className="absolute bottom-6 left-6 right-6 translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-sm font-bold text-white backdrop-blur-md transition-colors hover:bg-white/30">
                          Read Article <span className="text-lg">&rarr;</span>
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="mb-3 inline-block rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary dark:bg-primary/20">
                        {post.category}
                      </span>
                      <h4 className="line-clamp-2 text-2xl font-bold leading-tight text-gray-900 transition-colors group-hover:text-primary dark:text-white dark:group-hover:text-primary">
                        {post.title}
                      </h4>
                      <div className="mt-3 flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-4 w-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 12h13.5A2.25 2.25 0 0 1 21 13.5v7.5" />
                        </svg>
                        {dateFormatter(post.createdAt)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
      {/* Comments Section */}
      <section className="border-t border-gray-100 bg-gray-50/50 py-24 dark:border-gray-800 dark:bg-black/20">
        <CommentSection blogId={id} />
      </section>

    </motion.div>
  );
};

export default DetailBlog;
